<template>
  <div class="cca-detail-page">

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width:3rem;height:3rem;"></b-spinner>
      <div class="mt-3 text-muted">ກຳລັງໂຫຼດຂໍ້ມູນ...</div>
    </div>

    <div v-else-if="!record" class="text-center py-5 text-muted">ບໍ່ພົບຂໍ້ມູນ</div>

    <template v-else>
      <!-- ─── Header ─── -->
      <div class="detail-header">
        <b-button variant="link" class="back-btn" @click="$router.go(-1)">
          <b-icon icon="arrow-left-circle-fill" font-scale="1.4"></b-icon>
        </b-button>
        <div class="header-info">
          <h4 class="mb-0 font-weight-bold">{{ g('schoolName','school','meta_schoolName') }}</h4>
          <small class="text-light opacity-75">
            {{ g('schoolCode','meta_schoolCode') }} &nbsp;|&nbsp;
            {{ g('villageName','village','meta_village') }}, {{ g('district','meta_district') }}, {{ g('province') }}
          </small>
        </div>
        <div class="ml-auto">
          <b-badge variant="light" class="px-3 py-2">
            <b-icon icon="calendar3" class="mr-1"></b-icon>
            {{ g('collectionDate','date','meta_date') | fdate }}
          </b-badge>
        </div>
      </div>

      <!-- ─── Body ─── -->
      <div class="p-3">

        <!-- ── Row 1: Info + GPS ── -->
        <b-row class="mb-3">
          <!-- Collector Info -->
          <b-col md="6" class="mb-3">
            <b-card class="info-card h-100">
              <template #header>
                <span class="card-section-title">
                  <b-icon icon="person-badge-fill" class="mr-2 text-primary"></b-icon> ຜູ້ເກັບຂໍ້ມູນ
                </span>
              </template>
              <div class="info-row"><span class="lbl">ຊື່ຜູ້ເກັບ:</span><span>{{ g('collectorName','interviewer','meta_interviewer') }}</span></div>
              <div class="info-row"><span class="lbl">ເບີໂທ:</span><span>{{ g('collectorPhone') || '—' }}</span></div>
              <div class="info-row"><span class="lbl">ວັນທີ:</span><span>{{ g('collectionDate','date','meta_date') | fdate }}</span></div>
              <div class="info-row"><span class="lbl">ບັນທຶກໂດຍ:</span><span>{{ record.update_by || '—' }}</span></div>
              <div class="info-row"><span class="lbl">ອັບເດດ:</span><span>{{ record.update_at | fdate }}</span></div>
            </b-card>
          </b-col>

          <!-- GPS -->
          <b-col md="6" class="mb-3">
            <b-card class="info-card h-100">
              <template #header>
                <span class="card-section-title">
                  <b-icon icon="geo-alt-fill" class="mr-2 text-danger"></b-icon> ທີ່ຕັ້ງ GPS
                </span>
              </template>
              <div v-if="hasGps">
                <div class="gps-coords">
                  <div class="gps-chip"><span class="gps-lbl">LAT</span><strong>{{ lat }}</strong></div>
                  <div class="gps-chip"><span class="gps-lbl">LON</span><strong>{{ lon }}</strong></div>
                </div>
                <div class="mt-3 text-center">
                  <a :href="mapUrl" target="_blank" class="btn btn-outline-danger btn-sm rounded-pill px-4">
                    <b-icon icon="map-fill" class="mr-1"></b-icon> ເບິ່ງໃນ Google Maps
                  </a>
                  <div class="mt-3">
                    <iframe
                      :src="embedUrl"
                      width="100%"
                      height="180"
                      style="border:0;border-radius:8px;"
                      allowfullscreen=""
                      loading="lazy"
                    ></iframe>
                  </div>
                </div>
              </div>
              <div v-else class="text-center text-muted py-4">
                <b-icon icon="geo" font-scale="2.5" class="mb-2"></b-icon>
                <div>ບໍ່ມີຂໍ້ມູນ GPS</div>
              </div>
            </b-card>
          </b-col>
        </b-row>

        <!-- ── Photos ── -->
        <b-card class="mb-3 info-card" v-if="hasPhotos">
          <template #header>
            <span class="card-section-title">
              <b-icon icon="images" class="mr-2 text-warning"></b-icon> ຮູບພາບ ({{ photoList.length }} ຮູບ)
            </span>
          </template>
          <div class="photo-grid">
            <div
              v-for="(p, i) in photoList"
              :key="i"
              class="photo-item"
              @click="openPhoto(p)"
            >
              <img :src="p.src" :alt="p.label" class="photo-thumb" />
              <div class="photo-caption">{{ p.label }}</div>
            </div>
          </div>
        </b-card>

        <!-- ── Form Sections ── -->
        <b-card class="mb-3 info-card">
          <template #header>
            <span class="card-section-title">
              <b-icon icon="list-check" class="mr-2 text-success"></b-icon> ລາຍລະອຽດແບບສອບຖາມ
            </span>
          </template>
          <div v-for="(section, si) in formSections" :key="si" class="form-section-block">
            <div class="form-section-title">{{ section.title }}</div>
            <table class="q-table">
              <tbody>
                <tr v-for="(q, qi) in section.questions" :key="qi">
                  <td class="q-num-cell">{{ q.num }}</td>
                  <td class="q-label-cell">{{ q.label }}</td>
                  <td class="q-val-cell">{{ displayVal(q.key) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-card>
      </div>
    </template>

    <!-- Lightbox Modal -->
    <b-modal v-model="showPhoto" hide-footer hide-header size="xl" body-class="p-0 bg-dark text-center">
      <div class="position-relative">
        <b-button variant="light" class="lightbox-close" @click="showPhoto=false">✕</b-button>
        <img v-if="activePhoto.src" :src="activePhoto.src" style="max-width:100%;max-height:90vh;object-fit:contain;" />
        <div class="text-white py-2">{{ activePhoto.label }}</div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

// ── Flat lookup: code → Lao label (merged from all choice lists) ──
const CHOICE_LABELS = {
  // Consent
  yes: '✅ ແມ່ນ / ມີ', no: '❌ ບໍ່ແມ່ນ / ບໍ່ມີ', dk: '❓ ບໍ່ຮູ້',
  // Access level
  A: 'A. ຕົວເມືອງ',
  B: 'B. ຊົນນະບົດ — ມີເສັ້ນທາງ',
  C: 'C. ຊົນນະບົດ — ບໍ່ມີເສັ້ນທາງ',
  // Access season
  all_year: 'ເຂົ້າໄດ້ຕະຫຼອດປີ',
  rainy_issues: 'ເຂົ້າໄດ້ຕະຫຼອດ ແຕ່ມີບັນຫາຊ່ວງລະດູຝົນ',
  dry_only: 'ເຂົ້າເຖິງໄດ້ສະເພາະລະດູແລ້ງ',
  // Transport
  car: 'ລົດໃຫຍ່ (3 ລໍ້ຂຶ້ນໄປ)',
  motorbike: 'ລົດຈັກ (2 ລໍ້)',
  none: 'ບໍ່ສາມາດເຂົ້າເຖິງໄດ້',
  // Market distance
  '0_1': '0–1 Km', '2_5': '2–5 Km', '6_10': '6–10 Km', '10plus': 'ຫຼາຍກວ່າ 10 Km',
  // Income / livelihood
  farming: 'ເຮັດນາ / ເຮັດໄຮ່', tourism: 'ທ່ອງທ່ຽວ', industrial: 'ພືດອຸດສາຫະກຳ',
  livestock: 'ລ້ຽງສັດ', fishing: 'ການປະມົງ', mining: 'ຂຸດຄົ້ນບໍ່ແຮ່',
  cash_crops: 'ພືດເສດຖະກິດ', forest: 'ຫາເຄື່ອງປ່າ', trade: 'ຄ້າຂາຍ',
  migrant_work: 'ແຮງງານເຄື່ອນຍ້າຍ', factory: 'ກຳມະກອນໂຮງງານ',
  daily_wage: 'ແຮງງານລາຍວັນ', weaving: 'ເຮັດແຜ່ນແພ / ຕ່ໍາຫູກ',
  // Disaster types
  flood: 'ໄພນ້ຳຖ້ວມ', drought: 'ໄພແຫ້ງແລ້ງ', pest: 'ໄພຈາກສັດຕູພືດ',
  price_rise: 'ລາຄາຂຶ້ນ', illness: 'ການເຈັບປ່ວຍ',
  // Disaster frequency
  never: 'ບໍ່ເຄີຍ', rare: 'ດົນໆເທື່ອ (5 ປີ+)', sometimes: 'ສອງສາມປີເທື່ອ',
  often: 'ຢ່າງໜ້ອຍໜຶ່ງຄັ້ງຕໍ່ປີ',
  // Pct ranges
  '0_25': '0–25%', '1_25': '1–25%', '26_50': '26–50%', '51_75': '51–75%', '76_100': '76–100%',
  // Recovery
  self: 'ດ້ວຍຕົນເອງ', loan: 'ຢືມ/ກູ້ຢືມ', formal_aid: 'ໄດ້ຮັບຄວາມຊ່ວຍເຫຼືອ',
  temp_move: 'ຍ້າຍ (ຊົ່ວຄາວ)', perm_move: 'ຍ້າຍ (ຖາວອນ)',
  // Food security
  shortage: '⚠️ ຂາດແຄນ', sufficient: '✅ ພໍຢູ່',
  // Months
  '01': 'ມັງກອນ', '02': 'ກຸມພາ', '03': 'ມີນາ', '04': 'ເມສາ',
  '05': 'ພຶດສະພາ', '06': 'ມິຖຸນາ', '07': 'ກໍລະກົດ', '08': 'ສິງຫາ',
  '09': 'ກັນຍາ', '10': 'ຕຸລາ', '11': 'ພະຈິກ', '12': 'ທັນວາ',
  // Infrastructure items
  handwash: 'ບ່ອນລ້າງມື', toilet: 'ຫ້ອງນ້ຳ', dining: 'ບ່ອນກິນເຂົ້າ',
  kitchen: 'ເຮືອນຄົວ', storage: 'ສາງອາຫານ', water_source: 'ແຫຼ່ງນ້ຳ',
  // Garden care
  school_staff: 'ຄູ / ພະນັກງານ', parents: 'ພໍ່ແມ່ນັກຮຽນ', community: 'ຊຸມຊົນ',
  // No garden reasons
  no_land: 'ບໍ່ມີພື້ນທີ່', bad_soil: 'ດິນບໍ່ເໝາະ', no_water: 'ບໍ່ມີນ້ຳ',
  no_leader: 'ບໍ່ມີຜູ້ນຳ', no_community: 'ຂາດຊຸມຊົນ',
  // Water availability
  year_round: 'ມີຕະຫຼອດ', insufficient: 'ມີ ແຕ່ບໍ່ພໍ',
  not_drinkable: 'ໃຊ້ດື່ມບໍ່ໄດ້', broken: 'ແຫຼ່ງນ້ຳເສຍ', no_source: 'ບໍ່ມີແຫຼ່ງນ້ຳ',
  // Water sources
  piped_year: 'ນ້ຳສ້າງ (ຕະຫຼອດ)', piped_rain: 'ນ້ຳສ້າງ (ລະດູຝົນ)',
  gravity_year: 'ນ້ຳລີນ (ຕະຫຼອດ)', gravity_rain: 'ນ້ຳລີນ (ລະດູຝົນ)',
  pond_year: 'ໜອງ (ຕະຫຼອດ)', pond_rain: 'ໜອງ (ລະດູຝົນ)',
  other_year: 'ແຫຼ່ງອື່ນ (ຕະຫຼອດ)', other_partial: 'ແຫຼ່ງອື່ນ (ບາງລະດູ)',
  // Harvest pct
  // Food purchase sources
  nearest_market: 'ຕະຫຼາດໃກ້', farther_market: 'ຕະຫຼາດໄກ',
  local_farmers: 'ກະສິກອນທ້ອງຖິ່ນ', producer_group: 'ກຸ່ມຜູ້ຜະລິດ',
  local_forage: 'ຫາອາຫານທ້ອງຖິ່ນ',
  // Contribution types
  cash: '💰 ເງິນສົດ', food: '🍚 ອາຫານ', labor: '🔨 ແຮງງານ',
  material: '🧱 ວັດສະດຸ',
  // Contribution frequency
  monthly: 'ລາຍເດືອນ', per_term: 'ລາຍພາກຮຽນ', yearly: 'ລາຍປີ',
  // Cooking frequency
  all: 'ທຸກມື້', '4_5': '4 ຄັ້ງ/5 ວັນ', '3_5': '3 ຄັ້ງ/5 ວັນ', '1_5': '1 ຄັ້ງ/5 ວັນ',
  // Language groups
  lao_tai: 'ລາວ-ໄຕ', mon_khmer: 'ມອນ-ຂະແມ',
  sino_tibetan: 'ຈີນ-ຕິເບດ', hmong_mien: 'ມົ້ງ-ມຽນ',
  // Leadership
  principal: 'ອຳນວຍການ', teacher: 'ຄູສອນ', village_head: 'ນາຍບ້ານ',
  womens_union: 'ສະຫະພັນແມ່ຍິງ', vedc: 'ສະມາຊິກ ຄພສບ',
  // VEDC meeting frequency
  more_monthly: 'ຫຼາຍກວ່າ 1 ຄັ້ງ/ເດືອນ',
  // Problem status
  solved: 'ແກ້ໄຂໄດ້', in_progress: 'ກຳລັງດຳເນີນ',
  failed: 'ພະຍາຍາມແຕ່ບໍ່ສຳເລັດ', not_tried: 'ຍັງບໍ່ດຳເນີນ',
  // Problem solve methods
  internal_meeting: 'ປະຊຸມ ຄພສບ', escalate: 'ປຶກສານາຍ/ອຳນວຍ',
  // Who solves
  head_not_village: 'ຫົວໜ້າ ຄພສບ', individual: 'ບຸກຄົນ',
  // Repair / report status
  no_needed: '❌ ບໍ່ — ບໍ່ຈຳເປັນ', no_pending: '⏳ ຍັງດຳເນີນ',
  progress: 'ຄວາມຄືບໜ້າ', expense: 'ການໃຊ້ຈ່າຍ',
  // Cash amounts
  '0_50k': '0–50,000 ກີບ', '51_100k': '51–100,000 ກີບ', '100kplus': '>100,000 ກີບ',
  // Low contrib reasons
  no_resources: 'ບໍ່ມີຊັບພະຍາກອນ', unclear_goals: 'ເປົ້າໝາຍບໍ່ຊັດ',
  no_transparency: 'ຂາດຄວາມໂປ່ງໃສ',
  // Livestock
  no_infra: 'ພື້ນຖານ/ອຸປະກອນບໍ່ພໍ', no_food: 'ອາຫານບໍ່ພໍ',
  no_fresh_food: 'ອາຫານສົດ/ແຫ້ງບໍ່ພໍ', no_cook: 'ບໍ່ມີພໍ່ຄົວ',
  school_not_interested: 'ໂຮງຮຽນບໍ່ສົນໃຈ', student_not_interested: 'ນ/ຮ ເອົາເຂົ້າມາເອງ',
  school_closed: 'ໂຮງຮຽນປິດ', teacher_absent: 'ຄູຂາດສອນ',
  other: 'ອື່ນໆ',
};

const PHOTO_KEYS = [
  { key: 'school_wide',     label: 'ພາບກ້ວາງ' },
  { key: 'school_building', label: 'ອາຄານ' },
  { key: 'handwash',        label: 'ຈຸດລ້າງມື' },
  { key: 'toilet',          label: 'ຫ້ອງນ້ຳ' },
  { key: 'dining',          label: 'ສະຖານທີ່ກິນ' },
  { key: 'kitchen',         label: 'ເຮືອນຄົວ' },
  { key: 'storage',         label: 'ສາງ' },
  { key: 'water',           label: 'ນ້ຳ' },
  { key: 'garden',          label: 'ສວນຄົວ' },
  { key: 'animals',         label: 'ສັດ' },
  { key: 'fishpond',        label: 'ໜອງ' },
  { key: 'other',           label: 'ອື່ນໆ' },
];

export default {
  name: 'CCADetail',
  filters: {
    fdate(v) { return v ? moment(v).format('DD/MM/YYYY') : '—'; }
  },
  data() {
    return {
      loading: true,
      record: null,
      showPhoto: false,
      activePhoto: { src: '', label: '' },
      formSections: [
        {
          title: 'I. ການອະນຸຍາດ',
          questions: [
            { num: '1.1', label: 'ອ່ານບົດເວົ້າແນະນຳໂຕແລ້ວ?', key: 'q1_1' },
            { num: '1.2', label: 'ຕົກລົງເຫັນດີຕອບຄຳຖາມ?', key: 'q1_2' },
          ]
        },
        {
          title: 'II. ຂໍ້ມູນປະຊາກອນ',
          questions: [
            { num: '2.1', label: 'ຈຳນວນຜູ້ເຂົ້າຮ່ວມສົນທະນາ', key: 'q2_1' },
            { num: '2.2', label: 'ຈຳນວນຄົວເຮືອນໃນບ້ານ', key: 'q2_2' },
            { num: '2.3', label: 'ຈຳນວນປະຊາກອນທັງໝົດ', key: 'q2_3' },
            { num: '2.4', label: 'ຈຳນວນແມ່ຍິງ', key: 'q2_4' },
            { num: '2.5', label: 'ພາສາທີ່ໃຊ້ສ່ວນໃຫຍ່', key: 'q2_5' },
            { num: '2.6', label: 'ຊົນເຜົ່າສ່ວນໃຫຍ່', key: 'q2_6' },
            { num: '2.7', label: 'ຜູ້ນຳພາວຽກອາຫານທ່ຽງ', key: 'q2_7' },
          ]
        },
        {
          title: 'III. ຂໍ້ມູນຄວາມສ່ຽງ',
          questions: [
            { num: '3.1', label: 'ລະດັບການເຂົ້າເຖິງ (A/B/C)', key: 'q3_1' },
            { num: '3.2', label: 'ເຂົ້າໄດ້ຕະຫຼອດປີ ຫຼ ລະດູແລ້ງ?', key: 'q3_2' },
            { num: '3.3', label: 'ລະດັບຄວາມສະດວກໃນການເຂົ້າເຖິງ', key: 'q3_3' },
            { num: '3.4', label: 'ໄລຍະທາງໄປຕະຫຼາດໃກ້ທີ່ສຸດ', key: 'q3_4' },
            { num: '3.5', label: 'ວິທີສ້າງລາຍຮັບສ່ວນໃຫຍ່', key: 'q3_5' },
            { num: '3.6', label: 'ວິທີສ້າງລາຍຮັບອື່ນໆ', key: 'q3_6' },
            { num: '3.7', label: 'ເຄີຍປະສົບໄພພິບັດໃນໜຶ່ງປີ?', key: 'q3_7' },
            { num: '3.8', label: 'ປະເພດໄພພິບັດ', key: 'q3_8' },
            { num: '3.9', label: 'ຄວາມຖີ່ຂອງໄພພິບັດ', key: 'q3_9' },
            { num: '3.10', label: 'ໄພພິບັດກະທົບຕໍ່ໂຮງຮຽນ?', key: 'q3_10' },
            { num: '3.11', label: 'ກ່ຽວກັບການຮຽນ', key: 'q3_11' },
            { num: '3.12', label: 'ປະເພດຜົນກະທົບ', key: 'q3_12' },
            { num: '3.13', label: 'ພາວະຂາດອາຫານໃນໜຶ່ງປີ?', key: 'q3_13' },
            { num: '3.14', label: 'ຊ່ວງເວລາຂາດອາຫານ', key: 'q3_14' },
            { num: '3.15', label: 'ສາເຫດຂາດອາຫານ', key: 'q3_15' },
          ]
        },
        {
          title: 'IV. ໂຄງລ່າງພື້ນຖານ',
          questions: [
            { num: '4.1', label: 'ສິ່ງອຳນວຍຄວາມສະດວກທີ່ມີ', key: 'q4_1' },
            { num: '4.2', label: 'ມີສວນຄົວ?', key: 'q4_2' },
            { num: '4.2.1', label: 'ໃຜຈັດການສວນ?', key: 'q4_2_1' },
            { num: '4.2.2', label: 'ຜະລິດຕະຜົນຈາກສວນ', key: 'q4_2_2' },
            { num: '4.2.3', label: 'ຂະໜາດສວນ (ມ²)', key: 'q4_2_3' },
            { num: '4.2.4', label: 'ສ່ວນໃດຂອງຜະລິດຕະຜົນໄປທີ່ອາຫານທ່ຽງ (%)', key: 'q4_2_4' },
            { num: '4.3', label: 'ມີການລ້ຽງສັດ?', key: 'q4_3' },
            { num: '4.3.1', label: 'ຈຳນວນ ແລະ ປະເພດສັດ', key: 'q4_3_1' },
            { num: '4.4', label: 'ມີໜອງປາ?', key: 'q4_4' },
            { num: '4.5', label: 'ແຫຼ່ງນ້ຳຫຼັກ', key: 'q4_5' },
            { num: '4.6', label: 'ໄລຍະທາງໄປແຫຼ່ງນ້ຳ', key: 'q4_6' },
            { num: '4.7', label: 'ປະເພດເຊື້ອໄຟ', key: 'q4_7' },
            { num: '4.8', label: 'ບ່ອນເກັບຊື້ / ໄລຍະທາງ', key: 'q4_8' },
            { num: '4.9', label: 'ຊ່ວງທີ່ເຮືອນຄົວໃຊ້ (ອາທິດ/ທ່ຽງ/ປີ)', key: 'q4_9' },
            { num: '4.10', label: 'ເຮືອນຄົວໄດ້ໃຊ້ (ຄັ້ງ/ອາທິດ)', key: 'q4_10' },
            { num: '4.11', label: 'ເຮືອນຄົວໄດ້ໃຊ້ (ສ່ວນ/ຮຽນ)', key: 'q4_11' },
          ]
        },
        {
          title: 'V. ການປະກອບສ່ວນ',
          questions: [
            { num: '5.1', label: 'ຮູບແບບການປະກອບສ່ວນ', key: 'q5_1' },
            { num: '5.2', label: 'ເງິນສົດ (ກີບ/ນັກຮຽນ/ທ່ຽງ)', key: 'q5_2' },
            { num: '5.3', label: 'ເງິນສົດ (ກີບ/ນັກຮຽນ/ເດືອນ)', key: 'q5_3' },
            { num: '5.4', label: 'ເງິນສົດ (ກີບ/ນັກຮຽນ/ທ່ຽງ) – ຄ/ຮ', key: 'q5_4' },
            { num: '5.5', label: 'ເງິນສົດ (ກີບ/ນັກຮຽນ/ສ່ວນຮຽນ) – ຄ/ຮ', key: 'q5_5' },
            { num: '5.6', label: 'ອາຫານ (ກກ/ນັກຮຽນ/ທ່ຽງ) – ຊ/ຮ', key: 'q5_6' },
            { num: '5.7', label: 'ອາຫານ (ກກ/ນັກຮຽນ/ທ່ຽງ) – ຄ/ຮ', key: 'q5_7' },
            { num: '5.8', label: 'ຄ່າແຮງງານ (ກີບ/ມື້/ຄ/ຮ)', key: 'q5_8' },
            { num: '5.9', label: 'ຄ/ຮ ເຮັດວຽກໃຫ້ (ຄົນ/ມື້/ທ່ຽງ)', key: 'q5_9' },
            { num: '5.10', label: 'ວັດສະດຸ (ປະເພດ / ມູນຄ່າ)', key: 'q5_10' },
            { num: '5.11', label: 'ອື່ນໆ (ລາຍລະອຽດ)', key: 'q5_11' },
            { num: '5.12', label: 'ອື່ນໆ (ມູນຄ່າ)', key: 'q5_12' },
          ]
        },
        {
          title: 'VI. ຄະນະການ ພາລະກິດ ສ້ອມໂຮງຮຽນ (ຄພສບ)',
          questions: [
            { num: '6.1', label: 'ຈຳນວນສະມາຊິກ ຄພສບ ທັງໝົດ', key: 'q6_1' },
            { num: '6.2', label: 'ຈຳນວນແມ່ຍິງ', key: 'q6_2' },
            { num: '6.3', label: 'ມີລະບຽບ ຄພສບ ເປັນລາຍລັກ?', key: 'q6_3' },
            { num: '6.4', label: 'ມີການປະຊຸມ ຄພສບ ໃນ 6 ເດືອນ?', key: 'q6_4' },
            { num: '6.5', label: 'ຈຳນວນຄັ້ງປະຊຸມ', key: 'q6_5' },
            { num: '6.6', label: 'ຊ່ວຍຈັດຊື້ 5 ໝວດຫຼັກ?', key: 'q6_6' },
            { num: '6.6 (ລາຍລະອຽດ)', label: 'ໝວດທີ່ເຂົ້າຮ່ວມ', key: 'q6_6_desc' },
            { num: '6.7', label: 'ຄວບຄຸມຄຸນນະພາບ?', key: 'q6_7' },
            { num: '6.8', label: 'ຕິດຕາມ/ຊ່ວຍເຫຼືອ ແມ່ຄົວ?', key: 'q6_8' },
            { num: '6.9', label: 'ທ/ດ ກວດເມນູ?', key: 'q6_9' },
            { num: '6.10', label: 'ກວດເບິ່ງຍອດຄ/ຮ?', key: 'q6_10' },
            { num: '6.11', label: 'ຄ/ຮ ຕິດຕາມ/ດູແລ ນ/ຮ ກິນ?', key: 'q6_11' },
            { num: '6.12', label: 'ຕິດຕາມ ຊ. ເຂົ້າຮຽນ ນ/ຮ?', key: 'q6_12' },
            { num: '6.13', label: 'ໄດ້ຮັບການຝຶກອົບຮົມ ກ/ຮ?', key: 'q6_13' },
          ]
        },
        {
          title: 'VIII. ໝາຍເຫດ',
          questions: [
            { num: '8.1', label: 'ໝາຍເຫດ / ສັງເກດການ', key: 'q8_1' },
            { num: '8.2', label: 'ຄຳເຫັນເພີ່ມເຕີມ', key: 'q8_2' },
          ]
        },
      ]
    };
  },
  computed: {
    lat()    { return this.g('lat','latitude') || ''; },
    lon()    { return this.g('lon','longitude','lng') || ''; },
    hasGps() { return this.lat !== '' && this.lon !== ''; },
    mapUrl() { return `https://www.google.com/maps/search/?api=1&query=${this.lat},${this.lon}`; },
    embedUrl() {
      return `https://maps.google.com/maps?q=${this.lat},${this.lon}&z=15&output=embed`;
    },
    hasPhotos() { return this.photoList.length > 0; },
    photoList() {
      if (!this.record) return [];
      const photos = this.record.photos || {};
      const list = [];
      PHOTO_KEYS.forEach(({ key, label }) => {
        // support both photos.key and photos.key_1
        const src = photos[key] || photos[key + '_1'] || '';
        if (src) list.push({ src, label });
      });
      return list;
    }
  },
  mounted() {
    const id = this.$route.query.id;
    if (!id) { this.loading = false; return; }
    const user = JSON.parse(localStorage.getItem('Login') || '{}');
    axios.get(this.$store.getters.getBaseUrl + '/get_cca_one/' + id, {
      headers: { Authorization: 'Bearer ' + user.login_hash }
    }).then(res => {
      if (res.data.code === 1) this.record = res.data.data;
    }).catch(e => console.error(e))
    .finally(() => { this.loading = false; });
  },
  methods: {
    // get value by trying multiple keys
    g(...keys) {
      if (!this.record) return '';
      for (const k of keys) {
        const v = this.record[k];
        if (v !== undefined && v !== null && v !== '') return String(v);
      }
      return '';
    },
    displayVal(key) {
      if (!this.record) return '—';
      const v = this.record[key];
      if (v === undefined || v === null || v === '') return '—';
      const lookup = (code) => CHOICE_LABELS[String(code).trim()] || String(code).trim();
      if (Array.isArray(v)) return v.map(lookup).join(', ');
      const s = String(v);
      // handle comma-separated multi-select stored as string
      if (s.includes(',')) return s.split(',').map(lookup).join(', ');
      return lookup(s);
    },
    openPhoto(p) {
      this.activePhoto = p;
      this.showPhoto = true;
    }
  }
};
</script>

<style scoped>
.cca-detail-page {
  background: #f4f7f6;
  min-height: 100vh;
  font-family: 'Noto Sans Lao', sans-serif;
}
.detail-header {
  background: linear-gradient(135deg, #1B4F72, #2874A6);
  color: #fff;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.back-btn { color: #fff !important; padding: 0; }
.header-info small { opacity: 0.85; }
.info-card { border: none; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,.07); }
.info-card ::v-deep .card-header {
  background: #f8fafc;
  border-bottom: 1px solid #e9ecef;
  border-radius: 12px 12px 0 0 !important;
  padding: 10px 16px;
}
.card-section-title { font-weight: 700; font-size: .9rem; color: #344054; }
.info-row {
  display: flex;
  gap: 10px;
  padding: 5px 0;
  border-bottom: 1px dashed #f0f0f0;
  font-size: .9rem;
}
.info-row:last-child { border-bottom: none; }
.lbl { color: #888; min-width: 120px; }
.gps-coords { display: flex; gap: 12px; justify-content: center; }
.gps-chip {
  background: #f0f4ff;
  border: 1px solid #c9d9f5;
  border-radius: 8px;
  padding: 8px 16px;
  text-align: center;
}
.gps-lbl { display: block; font-size: .7rem; color: #888; font-weight: 600; }
/* Photos */
.photo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}
.photo-item {
  cursor: pointer;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0,0,0,.1);
  transition: transform .2s;
}
.photo-item:hover { transform: scale(1.04); }
.photo-thumb {
  width: 100%;
  height: 110px;
  object-fit: cover;
  display: block;
}
.photo-caption {
  background: rgba(0,0,0,.65);
  color: #fff;
  font-size: .78rem;
  text-align: center;
  padding: 4px;
}
/* Form table */
.form-section-block { margin-bottom: 20px; }
.form-section-title {
  font-weight: 700;
  font-size: .9rem;
  color: #1B4F72;
  background: #e7f1ff;
  padding: 6px 12px;
  border-left: 4px solid #1B4F72;
  border-radius: 0 6px 6px 0;
  margin-bottom: 6px;
}
.q-table { width: 100%; font-size: .88rem; border-collapse: collapse; }
.q-table tr { border-bottom: 1px solid #f0f0f0; }
.q-table tr:last-child { border-bottom: none; }
.q-num-cell  { width: 50px; color: #888; padding: 5px 8px; white-space: nowrap; }
.q-label-cell { color: #555; padding: 5px 8px; }
.q-val-cell  { font-weight: 600; padding: 5px 8px; color: #222; }
/* Lightbox */
.lightbox-close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  border-radius: 50%;
  width: 34px;
  height: 34px;
  padding: 0;
  line-height: 1;
  font-size: 1rem;
}
</style>
